import { Link } from "react-router-dom";
import { useTheme } from "@/lib/theme";
import MarketingNav from "@/components/Layout/MarketingNav";
import MarketingFooter from "@/components/Layout/MarketingFooter";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight, Sparkles, Briefcase, Users, Workflow,
  ShieldCheck, TrendingUp, ClipboardList, Check, Brain,
  Zap, MessageSquare, Timer, BarChart3, Layers, Building2,
} from "lucide-react";

/* Iter R138m · Home content expansion — AI, interdepartmental collaboration,
   productivity narrative, real-team photography, MSME-first framing.
   Images are Unsplash CDN (royalty-free) with fixed IDs so builds are stable. */

const IMG = {
  teamMeeting: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=80&auto=format&fit=crop",
  founder:     "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80&auto=format&fit=crop",
  operations:  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80&auto=format&fit=crop",
  standup:     "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80&auto=format&fit=crop",
  headshot1:   "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&q=80&auto=format&fit=crop",
  headshot2:   "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&q=80&auto=format&fit=crop",
  headshot3:   "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&q=80&auto=format&fit=crop",
};

const TESTIMONIALS = [
  {
    quote: "Our PM, HR and sales finally talk to each other — without anyone exporting a single CSV. We shut down four subscriptions in the first quarter.",
    name:  "Priya S.",
    role:  "Operations Lead · 60-person creative agency",
    photo: IMG.headshot1,
  },
  {
    quote: "The AI risk narratives caught a slipping deliverable two weeks before the client would have noticed. That is worth the license fee on its own.",
    name:  "Rahul M.",
    role:  "Founder · IT services firm, Pune",
    photo: IMG.headshot3,
  },
  {
    quote: "Onboarded 12 new hires in one quarter with the same HR person — the workflows just do the boring parts now.",
    name:  "Anita K.",
    role:  "People Ops · D2C brand, Bengaluru",
    photo: IMG.headshot2,
  },
];

export default function MarketingHome() {
  const { branding } = useTheme();
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/50">
        <div className="absolute inset-0 grain pointer-events-none opacity-60" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-28 grid lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-8">
            <div className="label-eyebrow mb-6 flex items-center gap-3" data-testid="hero-eyebrow">
              <span>Run · Grow · Scale</span>
              <span className="inline-flex items-center gap-1 chip chip-internal text-[10px]">
                <Sparkles className="w-3 h-3" /> AI-native · Built for MSMEs
              </span>
            </div>
            <h1 className="heading-display text-5xl sm:text-7xl xl:text-8xl mb-8 max-w-5xl" data-testid="hero-headline">
              The intelligent operating system for <span className="text-primary">growing businesses.</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed">
              {branding.platform_name} unifies projects, people, customers and processes in one AI-assisted workspace —
              so your teams stop fighting spreadsheets, chats and inboxes, and start compounding real output.
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <Link to="/signup">
                <Button size="lg" className="rounded-none h-12 px-6 text-sm font-bold tracking-wide uppercase" data-testid="hero-start-cta">
                  Start free <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="rounded-none h-12 px-6 text-sm font-bold tracking-wide uppercase" data-testid="hero-pricing-cta">
                  View pricing
                </Button>
              </Link>
              <span className="text-xs text-muted-foreground ml-2">No credit card. 10-minute setup. Cancel any time.</span>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex flex-col gap-3 text-sm">
            <div className="border border-border p-5">
              <div className="label-eyebrow mb-2">This week · a real workspace</div>
              <div className="font-medium">12 projects · 47 tasks closed · 3 invoices paid</div>
              <div className="text-muted-foreground text-xs mt-1">Studio Acme · across 4 teams · AI digest sent Monday</div>
            </div>
            <div className="border border-border p-5 bg-foreground text-background">
              <div className="label-eyebrow mb-2 opacity-70">One workspace</div>
              <div className="font-medium leading-tight">
                &ldquo;PM, HR and sales finally talk to each other — without anyone exporting a single CSV.&rdquo;
              </div>
              <div className="text-xs opacity-60 mt-2">Operations Lead · 60-person agency</div>
            </div>
            <div className="border border-border p-5">
              <div className="label-eyebrow mb-2">Live modules</div>
              <div className="flex gap-2 flex-wrap">
                <span className="chip chip-open">Projects</span>
                <span className="chip chip-resolved">HRMS</span>
                <span className="chip chip-approved">CRM</span>
                <span className="chip">Ops</span>
                <span className="chip chip-internal">AI Copilot</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF MARQUEE */}
      <section className="py-10 border-b border-border/50 overflow-hidden">
        <div className="text-center label-eyebrow mb-6">Made for the way MSMEs actually work</div>
        <div className="flex whitespace-nowrap marquee gap-16 text-2xl font-bold tracking-tighter opacity-50" style={{ fontFamily: "var(--font-heading)" }}>
          <span>SERVICE&nbsp;FIRMS&nbsp;·&nbsp;AGENCIES&nbsp;·&nbsp;STUDIOS&nbsp;·&nbsp;CONSULTANCIES&nbsp;·&nbsp;TRADING&nbsp;HOUSES&nbsp;·&nbsp;D2C&nbsp;BRANDS&nbsp;·&nbsp;EXHIBITIONS&nbsp;·&nbsp;</span>
          <span>SERVICE&nbsp;FIRMS&nbsp;·&nbsp;AGENCIES&nbsp;·&nbsp;STUDIOS&nbsp;·&nbsp;CONSULTANCIES&nbsp;·&nbsp;TRADING&nbsp;HOUSES&nbsp;·&nbsp;D2C&nbsp;BRANDS&nbsp;·&nbsp;EXHIBITIONS&nbsp;·&nbsp;</span>
        </div>
      </section>

      {/* PRODUCTIVITY METRICS BAND */}
      <section className="border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid md:grid-cols-4 gap-px bg-border">
          {[
            { k: "-38%", v: "time lost switching between apps", i: Timer },
            { k: "3.4×",  v: "faster inter-department handoffs",     i: Zap },
            { k: "+27%", v: "on-time delivery within one quarter",   i: TrendingUp },
            { k: "1 dashboard", v: "replaces the 5-tab morning ritual", i: BarChart3 },
          ].map((s, i) => (
            <div key={i} className="bg-background p-8" data-testid={`kpi-${i}`}>
              <s.i className="w-5 h-5 text-primary mb-4" />
              <div className="text-3xl font-black tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>{s.k}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.v}</div>
            </div>
          ))}
        </div>
        <div className="text-center text-[11px] text-muted-foreground pb-4">
          Averages across MSME customers in the first 90 days · self-reported operations audits.
        </div>
      </section>

      {/* FOUR PILLARS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-3 gap-3 mb-12 items-end">
          <div className="lg:col-span-2">
            <div className="label-eyebrow mb-4">One platform · Four pillars</div>
            <h2 className="heading-display text-4xl sm:text-5xl max-w-3xl">
              An ERP that fits an MSME — not the other way around.
            </h2>
          </div>
          <p className="text-muted-foreground text-sm">
            Replace four or five disconnected SaaS tools with one workspace your whole team actually opens every morning.
          </p>
        </div>
        <div className="grid lg:grid-cols-6 lg:grid-rows-2 gap-3">
          <div className="lg:col-span-3 lg:row-span-2 border border-border p-8 flex flex-col justify-between min-h-[420px] hover:border-primary/40 transition" data-testid="pillar-pm">
            <div>
              <Workflow className="w-6 h-6 text-primary mb-6" />
              <h3 className="font-bold text-2xl mb-3" style={{ fontFamily: "var(--font-heading)" }}>Project Management that ships work.</h3>
              <p className="text-muted-foreground max-w-md">
                Plans, milestones, tasks, subtasks and clear ownership — across clients, teams and departments.
                Less status-meeting theatre, more visible progress.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-8">
              <div className="aspect-square border border-border bg-muted/60 flex items-center justify-center text-xs label-eyebrow">Plans</div>
              <div className="aspect-square border border-border bg-muted/60 flex items-center justify-center text-xs label-eyebrow">Tasks</div>
              <div className="aspect-square border border-border bg-muted/60 flex items-center justify-center text-xs label-eyebrow">Goals</div>
              <div className="aspect-square border border-border bg-muted/60 flex items-center justify-center text-xs label-eyebrow">RAID</div>
              <div className="aspect-square border border-border bg-muted/60 flex items-center justify-center text-xs label-eyebrow">Reviews</div>
              <div className="aspect-square border border-border bg-foreground text-background flex items-center justify-center text-xs font-bold">Live</div>
            </div>
          </div>
          <div className="lg:col-span-3 border border-border p-8 hover:border-primary/40 transition" data-testid="pillar-hrms">
            <Users className="w-6 h-6 text-primary mb-4" />
            <h3 className="font-bold text-xl mb-2" style={{ fontFamily: "var(--font-heading)" }}>HRMS your people will use.</h3>
            <p className="text-sm text-muted-foreground">
              Employee records, attendance, leave, appraisals and recognition — the operational backbone for
              a growing team, with self-service that stops flooding HR&rsquo;s inbox.
            </p>
          </div>
          <div className="lg:col-span-2 border border-border p-8 hover:border-primary/40 transition" data-testid="pillar-crm">
            <Briefcase className="w-6 h-6 text-primary mb-4" />
            <h3 className="font-bold text-xl mb-2" style={{ fontFamily: "var(--font-heading)" }}>CRM, end to end.</h3>
            <p className="text-sm text-muted-foreground">From first lead through outreach, quotation and final invoice — without exporting to a spreadsheet in between.</p>
          </div>
          <div className="lg:col-span-1 border border-border p-8 bg-foreground text-background hover:bg-primary hover:text-primary-foreground transition flex flex-col justify-between" data-testid="pillar-ops">
            <ClipboardList className="w-6 h-6 mb-4" />
            <div>
              <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "var(--font-heading)" }}>Process efficiency.</h3>
              <p className="text-xs opacity-80">Standard ways of working, by default.</p>
            </div>
          </div>
        </div>
      </section>

      {/* R268ttt · Feb 2026 — TWO-LANE VALUE PITCH.
          Business owners and team members buy for very different reasons.
          Owners want time back, margin visibility, and AI that scales the
          business without hiring. Teams want fewer status updates, less
          copy-paste, and career growth from doing meaningful work.
          Two-column split makes the ROI story readable to both audiences
          without either side feeling ignored. */}
      <section className="border-y border-border/50 bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
          <div className="text-center mb-16">
            <div className="label-eyebrow mb-4">Why owners AND teams both fall in love</div>
            <h2 className="heading-display text-4xl sm:text-5xl max-w-3xl mx-auto">
              The rare tool leadership <span className="text-primary">buys</span> and teams actually <span className="text-primary">defend</span>.
            </h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-3">
            {/* Owners lane */}
            <div className="border border-border p-10 bg-background flex flex-col" data-testid="lane-owners">
              <div className="label-eyebrow mb-4 flex items-center gap-2">
                <Briefcase className="w-3 h-3" /> For founders & operators
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                Get your <span className="text-primary">Sunday nights</span> back.
              </h3>
              <p className="text-muted-foreground text-sm mb-8 leading-relaxed">
                Stop stitching status updates from four tools every Sunday. {branding.platform_name} does
                the reconciliation for you — margin, effort, cashflow and risks in one Monday-morning briefing,
                delivered before your first coffee.
              </p>
              <ul className="space-y-4 text-sm flex-1">
                {[
                  ["Hire fewer, ship more", "AI agents draft outreach, tag leads, chase collections and roll up timesheets — capacity you'd otherwise pay 3 salaries for."],
                  ["Every rupee tracked", "Live P&L per project, per client, per team — spot a losing engagement before you send the next month's invoice."],
                  ["Compliance on autopilot", "GST, TDS, PF, ESI, appraisal cycles and audit trails handled quietly in the background."],
                  ["Scale without chaos", "The playbooks that worked at 15 people still work at 150 because the tool enforces them, not a shared drive."],
                ].map(([t, d], i) => (
                  <li key={i} className="flex gap-3" data-testid={`lane-owners-row-${i}`}>
                    <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <div className="font-semibold text-foreground/90">{t}</div>
                      <div className="text-muted-foreground text-xs mt-0.5 leading-relaxed">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-border/60 flex items-center gap-3">
                <div className="text-3xl font-black text-primary" style={{ fontFamily: "var(--font-heading)" }}>~9 hrs</div>
                <div className="text-xs text-muted-foreground">reclaimed per week per operator — reported by MSME customers after 90 days.</div>
              </div>
            </div>
            {/* Teams lane */}
            <div className="border border-border p-10 bg-background flex flex-col" data-testid="lane-teams">
              <div className="label-eyebrow mb-4 flex items-center gap-2">
                <Users className="w-3 h-3" /> For the people doing the work
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                Do the work you were <span className="text-primary">actually hired for</span>.
              </h3>
              <p className="text-muted-foreground text-sm mb-8 leading-relaxed">
                Fewer status meetings. Fewer copy-paste-into-a-spreadsheet Fridays. Fewer "did you get my email?" pings.
                Just clear ownership, honest deadlines and AI that removes the parts of your job you never wanted.
              </p>
              <ul className="space-y-4 text-sm flex-1">
                {[
                  ["AI writes the boring bits", "Meeting recaps, status updates, follow-up emails, invoice reminders — auto-drafted in your voice, ready to send."],
                  ["One place, not eight tabs", "Your tasks, your team's tasks, your leaves, your reviews and your inbox — no more context-switch tax."],
                  ["Effort you can prove", "Timesheets and outcomes visible to leadership without micromanagement — appraisals become data conversations, not gut-feels."],
                  ["Actual autonomy", "Playbooks and permissions let you take initiative without waiting for approval every step."],
                ].map(([t, d], i) => (
                  <li key={i} className="flex gap-3" data-testid={`lane-teams-row-${i}`}>
                    <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <div className="font-semibold text-foreground/90">{t}</div>
                      <div className="text-muted-foreground text-xs mt-0.5 leading-relaxed">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-border/60 flex items-center gap-3">
                <div className="text-3xl font-black text-primary" style={{ fontFamily: "var(--font-heading)" }}>–41%</div>
                <div className="text-xs text-muted-foreground">time in status meetings + spreadsheet gymnastics — first-quarter team surveys.</div>
              </div>
            </div>
          </div>
          {/* R268ttt · AI Agents strip — leadership's favourite line item. */}
          <div className="mt-3 border border-border p-10 bg-foreground text-background grid md:grid-cols-3 gap-8 items-center" data-testid="ai-agents-band">
            <div className="md:col-span-2">
              <div className="label-eyebrow mb-3 opacity-70 flex items-center gap-2">
                <Brain className="w-3 h-3" /> AI agents that actually work while you sleep
              </div>
              <h3 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>
                Deploy specialised AI agents for <span className="text-primary">sales, HR, ops and finance</span> in one afternoon.
              </h3>
              <p className="opacity-80 text-sm mt-4 max-w-2xl leading-relaxed">
                Lead qualifier, follow-up drafter, invoice chaser, timesheet reconciler, appraisal summariser, risk narrator —
                each one lives inside your workspace, sees your real data, and hands off cleanly to a human when it matters.
                No prompt engineering. No zapier chains.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { l: "Lead qualifier", v: "24/7" },
                { l: "Invoice chaser", v: "auto" },
                { l: "Risk narrator", v: "daily" },
                { l: "Recap drafter", v: "every meeting" },
              ].map((a, i) => (
                <div key={i} className="border border-white/15 p-4">
                  <div className="text-[10px] uppercase tracking-widest opacity-60">{a.l}</div>
                  <div className="text-lg font-bold mt-1">{a.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI INTELLIGENCE SECTION */}
      <section className="border-y border-border/50 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-4 opacity-70 flex items-center gap-2">
              <Brain className="w-3 h-3" /> Built-in intelligence
            </div>
            <h2 className="heading-display text-4xl sm:text-6xl mb-6">
              An AI layer that <span className="text-primary">actually earns its keep.</span>
            </h2>
            <p className="opacity-80 text-lg leading-relaxed mb-8">
              {branding.platform_name} embeds AI where it removes real drudgery — writing status updates, flagging risks,
              summarising client threads, drafting outreach, and turning raw workspace signal into a Monday-morning
              briefing you can act on before your first coffee.
            </p>
            <Link to="/features">
              <Button size="lg" variant="secondary" className="rounded-none h-12 px-6 text-sm font-bold tracking-wide uppercase" data-testid="ai-explore-btn">
                See the AI features <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {[
              { i: MessageSquare, t: "AI briefings & digests", d: "Auto-drafted daily and weekly digests per team, per client — every stakeholder gets the signal, no one gets the noise." },
              { i: Brain,         t: "Risk narratives",         d: "Detect slipping projects, effort overruns and quiet clients before they escalate — with a plain-English explanation." },
              { i: Sparkles,      t: "Sales copilot",           d: "AI-drafted follow-ups, meeting recaps and outreach sequences that sound like your team, at 3× the volume." },
              { i: Zap,           t: "Smart automations",       d: "Nudge overdue owners, escalate to leadership, roll up subtask effort — without writing a single Zap." },
            ].map((f, i) => (
              <div key={i} className="border border-white/15 p-6 hover:border-primary/60 transition" data-testid={`ai-card-${i}`}>
                <f.i className="w-5 h-5 text-primary mb-4" />
                <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "var(--font-heading)" }}>{f.t}</h3>
                <p className="text-sm opacity-75">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CROSS-DEPARTMENT COLLABORATION */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="label-eyebrow mb-4 flex items-center gap-2"><Layers className="w-3 h-3" /> Interdepartmental collaboration</div>
          <h2 className="heading-display text-4xl sm:text-5xl mb-6">
            One thread from sales &rarr; delivery &rarr; finance &rarr; support.
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed mb-8">
            Every deal becomes a project. Every project rolls up into a client relationship. Every hire is linked
            to the team that requested them. When sales closes on a Friday, delivery already sees the scope on Monday —
            no forwarded emails, no shared drive scavenger hunts.
          </p>
          <ul className="space-y-4 text-sm">
            {[
              "Deal closed in CRM auto-creates the delivery project, with owner, deadlines and margins pre-populated.",
              "Timesheets from delivery flow directly into invoicing and profitability dashboards.",
              "HR sees which teams are burning out before anyone hands in a resignation.",
              "Support tickets are visible to the account manager who owns the relationship — no more surprises on renewal calls.",
            ].map((line, idx) => (
              <li key={idx} className="flex gap-3 items-start" data-testid={`collab-row-${idx}`}>
                <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <span className="text-foreground/85">{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative">
            <img
              src={IMG.teamMeeting}
              alt="A cross-functional team collaborating around a laptop"
              loading="lazy"
              className="w-full aspect-[4/3] object-cover border border-border grayscale hover:grayscale-0 transition-all duration-700"
              data-testid="collab-hero-img"
            />
            <div className="absolute -bottom-4 -left-4 bg-foreground text-background p-4 max-w-xs hidden sm:block">
              <div className="label-eyebrow opacity-70 mb-1">Handoff time</div>
              <div className="text-2xl font-black" style={{ fontFamily: "var(--font-heading)" }}>4h → 12min</div>
              <div className="text-xs opacity-70 mt-1">Sales close → delivery kickoff</div>
            </div>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="border-y border-border/50 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
          <div className="label-eyebrow mb-4">The outcomes</div>
          <h2 className="heading-display text-4xl sm:text-5xl mb-16 max-w-3xl">
            Fewer tools. Tighter operations. Faster decisions.
          </h2>
          <div className="grid md:grid-cols-4 gap-px bg-border">
            {[
              { n: "01", t: "Run", d: "One workspace for projects, people and customers — no more switching between five logins a day.", i: Workflow },
              { n: "02", t: "See", d: "Live dashboards and AI digests roll up work, revenue and team activity. Decide on facts, not on feel.", i: TrendingUp },
              { n: "03", t: "Standardise", d: "Departments, SOPs and roles built in — so the way you work survives the next 10 hires.", i: ClipboardList },
              { n: "04", t: "Protect", d: "Granular roles, audit trails and a secure credentials vault baked into the foundation.", i: ShieldCheck },
            ].map((s) => (
              <div key={s.n} className="bg-background p-8" data-testid={`outcome-step-${s.n}`}>
                <s.i className="w-6 h-6 text-primary mb-6" />
                <div className="label-eyebrow mb-2">{s.n}</div>
                <h3 className="font-bold text-xl mb-2" style={{ fontFamily: "var(--font-heading)" }}>{s.t}</h3>
                <p className="text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS WITH PHOTOS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-6 items-end mb-12">
          <div className="md:col-span-2">
            <div className="label-eyebrow mb-4">From MSME operators</div>
            <h2 className="heading-display text-4xl sm:text-5xl">
              People, not personas.
            </h2>
          </div>
          <p className="text-muted-foreground text-sm">
            The best signal isn&rsquo;t a G2 badge — it&rsquo;s an operator who stopped opening five browser tabs to run
            their Monday review.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="border border-border p-8 flex flex-col justify-between min-h-[280px] hover:border-primary/40 transition" data-testid={`testimonial-${i}`}>
              <blockquote className="text-base leading-relaxed">&ldquo;{t.quote}&rdquo;</blockquote>
              <figcaption className="flex items-center gap-3 mt-8">
                <img
                  src={t.photo}
                  alt={t.name}
                  loading="lazy"
                  className="w-11 h-11 object-cover rounded-full border border-border"
                />
                <div>
                  <div className="text-sm font-bold">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="border-t border-border/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="label-eyebrow mb-4 flex items-center gap-2"><Building2 className="w-3 h-3" /> Why {branding.platform_name}</div>
            <h2 className="heading-display text-4xl sm:text-5xl mb-6">
              Built ground-up for the 10–500 person business.
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed">
              Most ERPs were designed for the Fortune 500 and then bolted on a &ldquo;small business&rdquo; SKU.
              {" "}{branding.platform_name} starts where you actually are: a fast-growing team that needs structure
              without the six-month rollout, the consultant army or the per-seat sticker shock.
            </p>
          </div>
          <ul className="space-y-5 text-sm">
            {[
              "Modules that switch on as you grow — start with PM, layer in HR and Sales when it's time.",
              "AI is embedded where drudgery lives — not stapled on as a chatbot in the corner.",
              "Designed for the person doing the work, not just the dashboard for the CEO.",
              "Indian-first billing, GST, currencies and tax math — global-ready when you are.",
              "Per-workspace branding, custom roles and a portal for clients out of the box.",
              "Honest, predictable pricing. No setup fees, no module-by-module upsell maze.",
            ].map((line, idx) => (
              <li key={idx} className="flex gap-3 items-start" data-testid={`why-row-${idx}`}>
                <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <span className="text-foreground/85">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid md:grid-cols-2 gap-12 items-center border-t border-border/50">
        <div>
          <div className="label-eyebrow mb-4">Start in 60 seconds</div>
          <h2 className="heading-display text-4xl sm:text-6xl">
            Give your business an operating layer.
          </h2>
        </div>
        <div className="space-y-4">
          <p className="text-muted-foreground text-lg">
            {branding.tagline} Sign up free, invite your team, and replace the next spreadsheet you were about to open.
          </p>
          <div className="flex gap-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-none h-12 px-6 text-sm font-bold tracking-wide uppercase" data-testid="cta-start-free-btn">
                Start free <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="rounded-none h-12 px-6 text-sm font-bold tracking-wide uppercase" data-testid="cta-see-plans-btn">
                See plans
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
